import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setDateRange } from '../../store/dateRangeSlice';
import { Calendar, ChevronDown, X, ChevronLeft, ChevronRight } from "lucide-react";
import { format, isBefore, isAfter, isSameDay, addMonths, subMonths } from 'date-fns';

const DateRangePicker = ({ disabled }) => {
  const dispatch = useDispatch();
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectionState, setSelectionState] = useState({
    start: new Date(startDate),
    end: new Date(endDate),
    tempStart: null,
    tempEnd: null
  });
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    setSelectionState({
      start: new Date(startDate),
      end: new Date(endDate),
      tempStart: null,
      tempEnd: null
    });
  }, [startDate, endDate]);

  const handleDateClick = (date) => {
    // If no start date selected
    if (!selectionState.tempStart) {
      setSelectionState({
        ...selectionState,
        tempStart: date,
        tempEnd: null
      });
    }
    // If start date is selected, set end date
    else {
      // Allow same date for single day selection
      if (isAfter(date, selectionState.tempStart) || isSameDay(date, selectionState.tempStart)) {
        setSelectionState({
          ...selectionState,
          tempEnd: date
        });
      } else {
        // If selected date is before start date, swap them
        setSelectionState({
          ...selectionState,
          tempStart: date,
          tempEnd: null
        });
      }
    }
  };

  const formatDate = (date) => format(date, 'MMM d, yyyy');
  const displayRange = `${formatDate(new Date(startDate))} - ${formatDate(new Date(endDate))}`;

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      const prevMonth = subMonths(new Date(currentYear, currentMonth, 1), 1);
      setCurrentMonth(prevMonth.getMonth());
      setCurrentYear(prevMonth.getFullYear());
    } else {
      const nextMonth = addMonths(new Date(currentYear, currentMonth, 1), 1);
      setCurrentMonth(nextMonth.getMonth());
      setCurrentYear(nextMonth.getFullYear());
    }
  };

  const renderMonth = () => {
    const monthStart = new Date(currentYear, currentMonth, 1);
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const startDay = monthStart.getDay();

    // Use temp dates if they exist, otherwise use the saved dates
    const effectiveStart = selectionState.tempStart || selectionState.start;
    const effectiveEnd = selectionState.tempEnd || selectionState.end;

    return (
      <div className="grid grid-cols-7 gap-1">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
          <div key={day} className="text-xs text-center text-gray-500 py-1">{day}</div>
        ))}

        {Array.from({ length: startDay }).map((_, i) => (
          <div key={`empty-${i}`} className="w-8 h-8"></div>
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const date = new Date(currentYear, currentMonth, i + 1);
          const isSelected = (effectiveStart && effectiveEnd) &&
            (date >= effectiveStart && date <= effectiveEnd);
          const isStart = effectiveStart && isSameDay(date, effectiveStart);
          const isEnd = effectiveEnd && isSameDay(date, effectiveEnd);
          const isToday = isSameDay(date, new Date());

          return (
            // <button
            //   key={i}
            //   onClick={() => handleDateClick(date)}
            //   className={`w-8 h-8 rounded-full text-sm flex items-center justify-center
            //     ${isSelected ? 'bg-blue-100' : ''}
            //     ${isStart ? 'bg-blue-500 text-white' : ''}
            //     ${isEnd ? 'bg-blue-500 text-white' : ''}
            //     ${isToday ? 'border border-blue-500' : ''}
            //     ${date.getDay() === 0 || date.getDay() === 6 ? 'text-gray-400' : 'text-gray-700'}
            //     hover:bg-blue-50 transition-colors`}
            // >
            //   {i + 1}
            // </button>
            <button
              key={i}
              onClick={() => !disabled && handleDateClick(date)}
              disabled={disabled}
              className={`w-8 h-8 rounded-full text-sm flex items-center justify-center
                    ${isSelected ? 'bg-blue-100' : ''}
                    ${isStart ? 'bg-blue-500 text-white' : ''}
                    ${isEnd ? 'bg-blue-500 text-white' : ''}
                    ${isToday ? 'border border-blue-500' : ''}
                    ${date.getDay() === 0 || date.getDay() === 6 ? 'text-gray-400' : 'text-gray-700'}
                    ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-blue-50'}
                    transition-colors`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        {/* <button
          onClick={() => setShowCalendar(!showCalendar)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-800 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-xs"
        >
          <Calendar className="w-4 h-4 text-blue-500" />
          <span>{displayRange}</span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showCalendar ? 'rotate-180' : ''}`} />
        </button> */}
        <button
          onClick={() => !disabled && setShowCalendar(!showCalendar)}
          disabled={disabled}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg shadow-xs
    ${disabled
              ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
              : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"}`}
        >
          <Calendar className="w-4 h-4 text-blue-500" />
          <span>{displayRange}</span>
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showCalendar ? 'rotate-180' : ''}`} />
        </button>

      </div>

      {showCalendar && (
        <div className="absolute z-20 mt-2 p-4 bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h3 className="text-sm font-semibold text-gray-800">
              {format(new Date(currentYear, currentMonth, 1), 'MMMM yyyy')}
            </h3>
            <button
              onClick={() => navigateMonth('next')}
              className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {renderMonth()}

          <div className="flex justify-between mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => {
                setShowCalendar(false);
                // Reset to original values
                setSelectionState({
                  start: new Date(startDate),
                  end: new Date(endDate),
                  tempStart: null,
                  tempEnd: null
                });
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (selectionState.tempStart) {
                  const endDateToUse = selectionState.tempEnd || selectionState.tempStart;
                  dispatch(setDateRange({
                    startDate: format(selectionState.tempStart, 'yyyy-MM-dd'),
                    endDate: format(endDateToUse, 'yyyy-MM-dd')
                  }));
                }
                setShowCalendar(false);
              }}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;